import { Link } from "@tanstack/react-router";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import {
  ArrowRight,
  Handshake,
  HeartHandshake,
  Layers,
  Mail,
  MessageCircle,
  MapPin,
  Router,
  Settings2,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// ── Capability Card ───────────────────────────────────────────────────────────

interface CapabilityCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const CapabilityCard = ({ icon: Icon, title, description }: CapabilityCardProps) => (
  <div className="flex flex-col gap-3 rounded-xl border border-neutral-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
    <Icon className="h-6 w-6 text-neutral-700" aria-hidden="true" />
    <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
    <p className="text-sm leading-relaxed text-neutral-600">{description}</p>
  </div>
);

// ── Value Item ────────────────────────────────────────────────────────────────

interface ValueItemProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const ValueItem = ({ icon: Icon, title, description }: ValueItemProps) => (
  <div className="flex gap-4">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
    <div>
      <h3 className="font-semibold text-neutral-900">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-neutral-600">{description}</p>
    </div>
  </div>
);

// ── Differentiator ────────────────────────────────────────────────────────────

interface DifferentiatorProps {
  label: string;
  detail: string;
}

const Differentiator = ({ label, detail }: DifferentiatorProps) => (
  <div className="flex items-start gap-3 rounded-lg border border-neutral-100 bg-white/60 p-4">
    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
    <div>
      <p className="text-sm font-semibold text-neutral-900">{label}</p>
      <p className="mt-0.5 text-sm text-neutral-500">{detail}</p>
    </div>
  </div>
);

// ── Service Card ──────────────────────────────────────────────────────────────

interface ServiceCardProps {
  to: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge: string;
  title: string;
  tagline: string;
  description: string;
}

const ServiceCard = ({ to, icon: Icon = Layers, badge, title, tagline, description }: ServiceCardProps) => (
  <Card className="flex flex-col border-neutral-200 transition-shadow hover:shadow-md">
    <CardHeader className="pb-2">
      <Icon className="mb-4 h-8 w-8 text-neutral-700" aria-hidden="true" />
      <Badge variant="outline" className="w-fit text-xs">
        {badge}
      </Badge>
      <CardTitle className="mt-2 text-xl">{title}</CardTitle>
      <p className="text-sm font-medium text-neutral-500">{tagline}</p>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col justify-between gap-4">
      <p className="text-sm leading-relaxed text-neutral-600">{description}</p>
      <Link
        to={to}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-opacity hover:opacity-80"
      >
        Learn more <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </CardContent>
  </Card>
);

const partners = [
  {
    name: "Refrens",
    href: "https://www.refrens.com",
    logo: "/partners/refrens.png",
    description:
      "A business operating system for invoicing, accounting, and day-to-day operations.",
  },
  {
    name: "Sophos",
    href: "https://www.sophos.com",
    logo: "/partners/sophos.svg",
    description:
      "Global leader in endpoint protection, next-gen antivirus, and managed detection & response.",
  },
  {
    name: "ESET",
    href: "https://www.eset.com",
    logo: "/partners/eset.svg",
    description:
      "Global leader in digital security — endpoint protection, threat detection, and network security.",
  },
];

// ── Landing Page ──────────────────────────────────────────────────────────────

const LandingPage = () => {
  usePageSeo(pageSeo.home);
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden py-24">
        <img
          src="/allimages/digital world.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(3px)" }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <motion.div
          className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp}>
            <Badge variant="outline" className="mb-6 border-white/30 text-xs font-medium text-neutral-300">
              Software · Information Security · Hardware & CCTV · Zimbabwe
            </Badge>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="mb-6 text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl"
          >
            Software that fits how your{" "}
            <span className="text-primary">business actually works</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-neutral-300"
          >
            We build custom software platforms, strengthen your information security, and deliver the
            networking and CCTV infrastructure your business runs on — one technology partner, built for
            how you actually work.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Button asChild size="lg">
              <a href="#services">
                See our services <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <Link to="/contact">Talk to us</Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* ── What we do ── */}
      <section className="w-full py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto mb-12 max-w-2xl text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">What we do</h2>
            <p className="mt-4 text-neutral-600">
              We partner with organisations to design and build software, strengthen information
              security, and deploy the hardware infrastructure that keeps operations running — from
              first requirements to long-term support.
            </p>
          </motion.div>
          <motion.div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              { icon: Settings2, title: "Custom Software", description: "We build to your requirements, not around a pre-packaged product. Every engagement starts with understanding your business first." },
              { icon: Layers, title: "Business Platforms", description: "End-to-end systems that unify operations — stock, customers, payments, reporting — in a single, integrated platform." },
              { icon: MessageCircle, title: "WhatsApp Integrations", description: "Native WhatsApp connectivity for customer-facing workflows, so your business meets customers where they already are." },
              { icon: ShieldCheck, title: "Information Security", description: "Risk assessments, security policy and controls, and ongoing monitoring to keep your systems and data protected." },
              { icon: Router, title: "Hardware & CCTV", description: "Supply, installation, and support for networking infrastructure and CCTV surveillance systems on site." },
              { icon: Wrench, title: "Long-term Support", description: "We stay involved after launch. Ongoing maintenance, iterative improvements, and a team that knows your system inside out." },
            ].map(({ icon, title, description }) => (
              <motion.div key={title} variants={fadeUp}>
                <CapabilityCard icon={icon} title={title} description={description} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="w-full bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto mb-12 max-w-2xl text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">Our services</h2>
            <p className="mt-4 text-neutral-600">
              Some of our services include:
            </p>
          </motion.div>
          <motion.div
            className="grid gap-6 md:grid-cols-2"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              {
                to: "/products/alpha",
                badge: "Business management",
                title: "Alpha",
                tagline: "Operations platform for distributed businesses",
                description: "Alpha helps organisations manage stock, customers, agents, and banking partners through a unified system with real-time visibility and WhatsApp-native customer communication.",
              },
              {
                to: "/products/estman",
                badge: "Property management",
                title: "Estman",
                tagline: "Property portfolio management for African markets",
                description: "Estman gives property managers, landlords, and agencies a single system for their full portfolio — properties, tenants, leases, payments, and maintenance requests.",
              },
              {
                to: "/contact",
                icon: ShieldCheck,
                badge: "Information security",
                title: "Information Security Services",
                tagline: "Protecting your systems, data, and operations",
                description: "We help organisations assess and strengthen their information security posture — covering risk assessments, policy and controls, and ongoing monitoring to keep your systems and data protected.",
              },
              {
                to: "/contact",
                icon: Router,
                badge: "Hardware & infrastructure",
                title: "Networking & CCTV",
                tagline: "Physical infrastructure for your business",
                description: "We supply, install, and support networking infrastructure and CCTV surveillance systems, giving your business reliable connectivity and site security alongside the software we build.",
              },
            ].map((s) => (
              <motion.div key={s.title} variants={fadeUp}>
                <ServiceCard {...s} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── How we work ── */}
      <section className="relative w-full overflow-hidden py-20">
        <img
          src="/allimages/customerconfrence.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(4px)" }}
        />
        <div className="absolute inset-0 bg-white/85" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-neutral-900">How we work</h2>
              <p className="mt-4 text-neutral-600">
                We treat every engagement as a long-term partnership, not a one-off
                transaction. That means staying involved, staying honest, and building
                something that keeps working.
              </p>
            </motion.div>
            <motion.div
              className="flex flex-col gap-6"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {[
                { icon: HeartHandshake, title: "We listen first", description: "Every project starts with deep discovery. We don't propose solutions until we understand the problem." },
                { icon: Handshake, title: "Partnerships, not projects", description: "We work alongside you, not just for you. Your team is part of the process from scoping through delivery." },
                { icon: Wrench, title: "Present after launch", description: "Software needs care. We provide ongoing support, improvements, and a team that knows your system." },
                { icon: MapPin, title: "Grounded in local context", description: "Built in Zimbabwe, our products and approach reflect the real conditions of doing business in this market." },
              ].map(({ icon, title, description }) => (
                <motion.div key={title} variants={fadeUp}>
                  <ValueItem icon={icon} title={title} description={description} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why Arsoc ── */}
      <section className="w-full bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto mb-12 max-w-2xl text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">Why work with Arsoc</h2>
            <p className="mt-4 text-neutral-600">
              A few things that set us apart — stated plainly.
            </p>
          </motion.div>
          <motion.div
            className="grid gap-4 sm:grid-cols-2"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              { label: "Built in Zimbabwe", detail: "Deep local context means fewer assumptions and software that actually fits the market." },
              { label: "WhatsApp-native by design", detail: "Our platforms integrate with WhatsApp from the ground up, not as an afterthought." },
              { label: "We stay after launch", detail: "Maintenance, iterations, and honest advice — we're a long-term partner, not a vendor." },
              { label: "Tailored, not templated", detail: "We build to your requirements. Every system we deliver is designed for the way you work." },
              { label: "Proven in production", detail: "Alpha is live with real organisations, handling real operations at scale." },
              { label: "Trusted relationship model", detail: "We are selective about the work we take on, so we can do every project properly." },
              { label: "One partner, full stack", detail: "Software, information security, and the networking and CCTV hardware underneath it — all from one team." },
              { label: "Backed by Rothkos Group", detail: "Arsoc is a member of the Rothkos Group, giving clients the backing of an established, multi-disciplinary group." },
            ].map(({ label, detail }) => (
              <motion.div key={label} variants={fadeUp}>
                <Differentiator label={label} detail={detail} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Partners ── */}
      <section id="partners" className="w-full py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto mb-12 max-w-2xl text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">Our partners</h2>
            <p className="mt-4 text-neutral-600">
              We work with trusted platforms and security vendors that complement the
              systems we build and the information security services we provide.
            </p>
          </motion.div>
          <motion.div
            className="flex flex-wrap items-stretch justify-center gap-6"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {partners.map((partner) => {
              const content = (
                <>
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={`${partner.name} logo`}
                      className="h-16 w-16 rounded-2xl object-contain"
                    />
                  ) : (
                    <span
                      className="flex h-16 w-16 items-center justify-center rounded-2xl bg-neutral-100 text-lg font-bold text-neutral-500"
                      aria-hidden="true"
                    >
                      {partner.name
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .slice(0, 2)}
                    </span>
                  )}
                  <p className="mt-4 text-2xl font-bold tracking-tight text-neutral-900">
                    {partner.name}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                    {partner.description}
                  </p>
                </>
              );
              const className =
                "flex h-full flex-col items-center rounded-xl border border-neutral-100 bg-white p-8 text-center shadow-sm transition-shadow hover:shadow-md";
              return (
                <motion.div key={partner.name} variants={fadeUp} className="w-full max-w-sm">
                  {partner.href ? (
                    <a
                      href={partner.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${partner.name} website`}
                      className={className}
                    >
                      {content}
                    </a>
                  ) : (
                    <div className={className}>{content}</div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Contact CTA ── */}
      <section className="relative w-full overflow-hidden py-20">
        <img
          src="/allimages/backgroundpicblury.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(2px)" }}
        />
        <div className="absolute inset-0 bg-neutral-950/90" />
        <motion.div
          className="relative z-10 mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 variants={fadeUp} className="text-3xl font-bold text-white">
            Have a project in mind?
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-lg text-neutral-300">
            Tell us what you're building. We'll tell you honestly whether we're the right
            fit — and if we are, we'll build it well.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90">
              <Link to="/contact">Start a conversation</Link>
            </Button>
            <a
              href="mailto:admin@arsoc.co.zw"
              className="inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              admin@arsoc.co.zw
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default LandingPage;
